import classNames from 'classnames';
import styles from './DIDDefinition.module.css';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Dropdown,
  MenuProps,
  Space,
  Checkbox,
  CheckboxProps,
  List,
  Spin,
} from 'antd';
import React, { useState, useRef, useImperativeHandle, useEffect, forwardRef } from 'react';
import Modal from '../../Modal';
import Icon from 'src/components/Icon/Icon';
import { getSearchResultFromQueryParameter } from 'src/app/query.service';
import { MODAL_STATUS } from 'src/app/constants';

interface FormData {
  [key: string]: any;
}

interface DIDDefinitionProps {
  isActive: boolean;
  onNext: (newData: { [key: string]: any }) => void;
  handleCheckbox: (decodeData: boolean) => void;
  checkboxValue: boolean;
  onBack: () => void;
  propData: FormData;
  form: any;
  queryType: any;
}

interface Module {
  id: string;
  module: string;
}
interface DidId {
  id: string;
  didId: string;
  didDescription: string;
}

// const placeholderMapping: { [key: string]: { [key: string]: string } } = {
//   module: { searchParameter: 'module', displayKey: 'module' },
//   didId: { searchParameter: 'didId', displayKey: 'didId' },
// };

const DIDDefinition = forwardRef<any, DIDDefinitionProps>(
  ({ isActive, onBack, onNext, handleCheckbox, checkboxValue, propData, form, queryType }, ref) => {
    const [openModal, setOpenModal] = useState(false);
    const [inputPlaceholder, setInputPlaceholder] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isSmallText, setIsSmallText] = useState(false);
    const [result, setResult] = useState<FormData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [module, setModule] = useState<FormData[]>([]);
    const [didId, setDidId] = useState<FormData[]>([]);
    const [sourceCheck, setSourceCheck] = useState<string>('');
    const [vinField, setVin] = useState<string>('');
    const [decodeData, setDecodeData] = useState(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const [tmpdata, setTmpData] = useState<{ [key: string]: any }>({});
    const checkBoxOnChange: CheckboxProps['onChange'] = (e) => {};
    const inputRef = useRef(null);

    const [selectedValues, setSelectedValues] = useState<{ [key: string]: any }>({
      module: [],
      didId: [],
    });

    const onFinish = (values: any) => {};

    useImperativeHandle(ref, () => ({
      reset() {
        setSelectedValues({
          module: [],
          didId: [],
        });
      },
    }));

    useEffect(() => {
      setDecodeData(checkboxValue);
      setSelectedValues((prevValues) => ({
        ...prevValues,
        ...propData,
      }));
    }, [propData]);

    const applyMethod = (type: string) => {
      if (type === 'module') {
        form.setFieldValue('module', selectedValues.module.join(', '));
      }
      if (type === 'didId') {
        form.setFieldValue('didId', selectedValues.didId.join(', '));
      }
      setOpenModal(false);
      setTmpData({});
      setSearchValue('');
      setIsInputFocused(false);
      setIsSmallText(false);
    };

    const handleCancel = () => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [sourceCheck]: [...(tmpdata[sourceCheck] || [])],
      }));
      setSearchValue('');
      setTmpData({});
      setOpenModal(false);
      setIsInputFocused(false);
      setIsSmallText(false);
    };

    const handleInputClick = async (placeholder: string) => {
      setInputPlaceholder(placeholder);
      setOpenModal(true);
      setSearchValue('');
      setIsInputFocused(false);
      setIsSmallText(false);
      form.setFieldValue('searchValues', '');

      switch (placeholder) {
        case 'Module':
          setSourceCheck('module');
          try {
            const searchParameter = 'module';
            setIsLoading(true);
            const moduleData: Module[] = await getSearchResultFromQueryParameter<Module>(
              queryType,
              searchParameter,
              selectedValues
            );
            setModule(moduleData);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'Did Id':
          setSourceCheck('didId');
          try {
            const searchParameter = 'didId';
            setIsLoading(true);
            const didIdData: DidId[] = await getSearchResultFromQueryParameter<DidId>(
              queryType,
              searchParameter,
              selectedValues
            );
            setDidId(didIdData);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
      }
    };

    const handleModalInputFocus = () => {
      setIsInputFocused(true);
      setIsSmallText(true);
    };

    const handleModalInputBlur = () => {
      if (!searchValue) {
        setIsInputFocused(false);
        setIsSmallText(false);
      }
    };

    const onLinkClick = (type: string, value: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: prevValues[type]?.includes(value)
          ? prevValues[type]
          : [...(prevValues[type] || []), value],
      }));
    };
    const handleRemove = (type: string, itemRemove: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: prevValues[type]?.filter((item: string) => item !== itemRemove),
      }));
    };

    const handleNext = () => {
      const data = { ...selectedValues };
      handleCheckbox(decodeData);
      onNext(data);
    };

    const handleInputClear = (type: string) => {
      form.setFieldValue(type, ``);
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: [],
      }));
    };

    const shouldShowResetButton = ['module', 'didId'].some((key) => {
      const value = selectedValues[key];
      return Array.isArray(value) ? value.length > 0 : value !== null;
    });

    const handlePlaceholderClick = () => {
      // Focus the input when the placeholder is clicked
      inputRef.current.focus();
    };

    const handleReset = () => {
      form.setFieldValue('module', '');
      form.setFieldValue('didId', '');
      setSelectedValues({
        module: [],
        didId: [],
      });
    };

    const moreItems: MenuProps['items'] = [
      {
        key: '1',
        label: '5',
      },
      {
        key: '2',
        label: '10',
      },
      {
        key: '3',
        label: '15',
      },
      {
        key: '4',
        label: '25',
      },
      {
        key: '4',
        label: '50',
      },
      {
        key: '4',
        label: '70',
      },
    ];

    const filteredSources = module.filter((source) =>
      source.module?.toLowerCase().includes(searchValue?.toLowerCase())
    );
    const filteredSourcesId = didId.filter((source) =>
      source.didId?.toLowerCase().includes(searchValue?.toLowerCase())
    );

    return (
      <div className={classNames(styles.DIDDefinition)}>
        <div
          className={`
            ${styles['DIDDefinition--QueryTitle']} 
            ${isActive ? styles['DIDDefinition--active'] : styles['DIDDefinition--InActive']}
            `}
          // style={{ backgroundColor: isActive ? '#000000' : '#dcdcdc' }}
        >
          DID Definition
          {shouldShowResetButton && (
            <div style={{ display: 'inline', float: 'right', marginRight: '30px' }}>
              <Button
                className={`${
                  isActive ? styles['DIDDefinition--Reset'] : styles['DIDDefinition--InActive']
                }`}
                type="primary"
                htmlType="submit"
                onClick={handleReset}
                disabled={!isActive}
              >
                Reset
              </Button>
            </div>
          )}
        </div>
        <div className={classNames(styles['DIDDefinition--FormContainer'])}>
          <Form
            form={form}
            name="did_definition_data_form"
            onFinish={onFinish}
            layout="horizontal"
            disabled={!isActive}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="module"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('Module')}
                    placeholder="Module"
                    suffix={
                      selectedValues.module?.length > 0 && (
                        <span onClick={() => handleInputClear('module')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.module?.length > 0 && (
                  <span
                    className={`${classNames(styles['DIDDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Module ({selectedValues.module?.length})
                  </span>
                )}
              </Col>

              <Col span={8}>
                <Form.Item
                  name="didId"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('Did Id')}
                    placeholder="DID ID (hex)"
                    suffix={
                      selectedValues.didId?.length > 0 && (
                        <span onClick={() => handleInputClear('didId')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.didId?.length > 0 && (
                  <span
                    className={`${classNames(styles['DIDDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    DID ID ({selectedValues.didId?.length})
                  </span>
                )}
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="didDecodeData" valuePropName="checked">
                  <Checkbox value={decodeData} onChange={(e) => setDecodeData(e.target.checked)}>
                    Tick box to include decoded DID data field in query results
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>

            {isActive && (
              <Row justify="space-between">
                <Col>
                  <Form.Item>
                    <Button
                      className={classNames(styles['DIDDefinition--Buttons'])}
                      type="primary"
                      onClick={onBack}
                    >
                      Back
                    </Button>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item>
                    <Button
                      className={classNames(styles['DIDDefinition--Buttons'])}
                      type="primary"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Modal open={openModal} onClose={() => setOpenModal(false)} width={900} footer={null}>
              <div className={classNames(styles['DIDDefinition--ModalHeader'])}>
                Select {inputPlaceholder}
                {inputPlaceholder === 'Status' && <span> - {MODAL_STATUS}</span>}
              </div>
              <div className={classNames(styles['DIDDefinition--ModelContent'])}>
                <div>
                  <p className={classNames(styles['DIDDefinition--TagTitle'])}>Selection</p>
                  <div className={classNames(styles['DIDDefinition--TagsContainer'])}>
                    {(selectedValues[sourceCheck] || []).map((item: any, index: any) => {
                      return (
                        <Button
                          type="primary"
                          key={`${item}${index}`}
                          onClick={() => handleRemove(sourceCheck, item)}
                          className={styles['selected-button']}
                        >
                          {item}
                          <Icon
                            name="close"
                            className={`${styles['DIDDefinition--ModalDropDownIcon']} ${styles['close-icon']}`}
                          />
                        </Button>
                      );
                    })}
                  </div>
                </div>
                <div className={styles['DIDDefinition--Search']}>
                  <Row gutter={16} justify="space-between">
                    <Col span={8}>
                      <div style={{ position: 'relative' }}>
                        <Input
                          name="filterValue"
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          onFocus={handleModalInputFocus}
                          onBlur={handleModalInputBlur}
                          ref={inputRef} // Use ref to access the input
                        />
                        <span
                          className={`${styles['DIDDefinition--ModalInputText']} ${
                            isSmallText ? styles.small : ''
                          }`}
                          onClick={handlePlaceholderClick} // Click handler to focus the input
                        >
                          Look for {inputPlaceholder}
                        </span>
                      </div>
                    </Col>
                    <Col span={4}>
                      <Dropdown menu={{ items: moreItems }} trigger={['click']}>
                        <Button className={styles['DIDDefinition--ModalDropDown']}>
                          Limit
                          <Icon
                            name="arrow_drop_down"
                            className={styles['DIDDefinition--ModalDropDownIcon']}
                          />
                        </Button>
                      </Dropdown>
                    </Col>
                  </Row>
                </div>
                <div className={styles['DIDDefinition--SearchList']}>
                  {sourceCheck === 'module' && (
                    <List
                      itemLayout="horizontal"
                      // loading={isLoading}
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={styles['loader-color']} />,
                      }}
                      dataSource={filteredSources}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.module?.includes(item.module)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => onLinkClick('module', item.module)}
                        >
                          <span>{item.module}</span>
                        </List.Item>
                      )}
                    />
                  )}
                  {sourceCheck === 'didId' && (
                    <List
                      itemLayout="horizontal"
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={styles['loader-color']} />,
                      }}
                      dataSource={filteredSourcesId}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.didId?.includes(item.didId)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => onLinkClick('didId', item.didId)}
                          style={{ display: 'flex', flexWrap: 'nowrap' }}
                        >
                          <div style={{ flex: 1, paddingRight: '10px' }}>
                            <span>{item.didId}</span>
                          </div>
                          <div style={{ flex: 4, paddingRight: '10px', whiteSpace: 'nowrap' }}>
                            <span>{item.didDescription}</span>
                          </div>
                        </List.Item>
                      )}
                    />
                  )}
                </div>
                <div className={styles['DIDDefinition--ModalPageNo']}> 1 - 4 / 4 </div>
              </div>
              <div className={styles['DIDDefinition--ModalFooter']}>
                <Space>
                  <Button onClick={handleCancel} className={styles['DIDDefinition--CancelButton']}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => applyMethod(sourceCheck)}
                    className={styles['DIDDefinition--Buttons']}
                  >
                    Apply
                  </Button>
                </Space>
              </div>
            </Modal>
          </Form>
        </div>
      </div>
    );
  }
);
DIDDefinition.displayName = 'DIDDefinition';
export default DIDDefinition;
